import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PayrollKind } from '@masamune/shared';
import { api } from '../../../lib/api';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { toast } from '../../../stores/toast.store';
import { confirm as confirmDialog } from '../../../stores/confirm.store';
import { daysFromToday, plurale, type MatchKind, type Paginated, type PropostaRic, type UscitaSource } from '../adminApi';

/**
 * Da dove viene il dubbio su un abbinamento (campo aggiunto dall'API nel lotto
 * 6): altre fatture che vogliono lo stesso bonifico, o altri bonifici uguali.
 * Il tipo `PropostaRic` sta in adminApi.ts, fuori da questa cartella: lo si
 * legge da qui senza toccarlo.
 */
export type Ambiguita = 'ALTRE_RATE' | 'ALTRI_MOVIMENTI' | null;
export const ambiguitaDi = (p: PropostaRic): Ambiguita => (p as PropostaRic & { ambiguita?: Ambiguita }).ambiguita ?? null;

/**
 * Banca (`/admin/banca`) — le chiamate e le parole della pagina.
 *
 * Un file suo, e non adminApi.ts / useAdminActions.ts, per due motivi:
 *  - le chiamate nuove del lotto 6 (`/admin/banca/sintesi`, `/commercialista`,
 *    `/contanti`, `/riconciliazione/collega`) servono solo qui;
 *  - i messaggi dopo un'azione parlano con le parole nuove della pagina
 *    ("Abbinamento confermato", "Bonifico collegato", niente "lotto" né
 *    "riconciliazione"), mentre quelli di useAdminActions restano per le altre
 *    pagine finché non vengono rifatte.
 * Tutte le chiavi stanno sotto ['admin', ...]: ogni azione dell'Amministrazione
 * che invalida ['admin'] aggiorna anche questa pagina.
 */

// ════════════════════════════════════════════════════════════════════
// Sintesi: frase, conti, pallini
// ════════════════════════════════════════════════════════════════════

export interface ContoBanca {
  id: string;
  nome: string;
  saldo: number;
  ibanFinale: string | null;
}

export interface BancaSintesi {
  /** ISO: l'ultima volta che Qonto ha dato i saldi. */
  aggiornataAlle: string | null;
  qontoCollegato: boolean;
  principale: ContoBanca | null;
  iva: ContoBanca | null;
  contanti: { id: string; saldo: number } | null;
  altri: { nomi: string[]; saldo: number };
  bonifici: { sicuri: number; sicuriIncassi: number; sicuriPagamenti: number; incerti: number };
  /** Quanti pagamenti segnati qui sono ancora da comunicare allo studio. */
  commercialista: number;
  /** Pagamenti segnati da soli negli ultimi 30 giorni (e non annullati). */
  automatici?: number;
  /** Il fido Qonto (26/10/2026): il conto principale può scendere fino a −fido. */
  fido?: number;
}

export const bancaSintesiQuery = {
  queryKey: ['admin', 'banca', 'sintesi'] as const,
  queryFn: () => api.get<BancaSintesi>('/admin/banca/sintesi'),
  staleTime: 30_000,
};

// ════════════════════════════════════════════════════════════════════
// Pagamenti segnati in automatico (admin.autochiusura.ts)
// ════════════════════════════════════════════════════════════════════

export type TipoAutomatico = 'INCASSO' | 'USCITA' | 'SPESA_FISSA';

/**
 * Una voce che si è segnata da sola dopo il sync Qonto: una fattura di un
 * cliente incassata, una fattura di un fornitore pagata, una spesa fissa
 * addebitata. `importo` è quello registrato (per le spese fisse, quello vero
 * addebitato; `previsto` è quello della scheda).
 */
export interface PagamentoAutomatico {
  runKey: string;
  key: string;
  tipo: TipoAutomatico;
  nome: string;
  cosa: string;
  importo: number;
  previsto: number | null;
  scadenza: string | null;
  bonifico: { id: string; data: string | null; nome: string; conto: string | null; importo: number; operationType: string | null };
  /** ISO: quando si è segnata. */
  segnataIl: string;
}

export const automaticiQuery = {
  queryKey: ['admin', 'banca', 'automatici'] as const,
  queryFn: () => api.get<{ giorni: number; items: PagamentoAutomatico[] }>('/admin/banca/automatici'),
  staleTime: 30_000,
};

/** Il chip del tipo, per esteso. */
export const TIPO_AUTOMATICO_LABEL: Record<TipoAutomatico, string> = {
  INCASSO: 'Fattura di un cliente',
  USCITA: 'Fattura di un fornitore',
  SPESA_FISSA: 'Spesa fissa',
};

/** Il bottone della riga: dice dove torna la voce, non "Annulla" e basta. */
export const RIMETTI_LABEL: Record<TipoAutomatico, string> = {
  INCASSO: 'Rimetti da incassare',
  USCITA: 'Rimetti da pagare',
  SPESA_FISSA: 'Rimetti da pagare',
};

// ════════════════════════════════════════════════════════════════════
// Da comunicare al commercialista
// ════════════════════════════════════════════════════════════════════

export type ComeSegnata = 'AUTOMATICO' | 'CONFERMATO' | 'A_MANO';

export interface PagamentoDaComunicare {
  lato: 'INCASSO' | 'USCITA';
  id: string;
  docId: string;
  numero: string | null;
  nome: string;
  ragioneSociale: string;
  rata: number;
  rateTotali: number;
  importo: number;
  pagataIl: string | null;
  segnataIl: string | null;
  come: ComeSegnata;
  perche: string | null;
  bonifico: { data: string | null; nome: string; conto: string | null } | null;
}

export interface Commercialista {
  items: PagamentoDaComunicare[];
  totali: { incassi: number; uscite: number };
  daComunicare: number;
  soloNostre: number;
}

export function commercialistaQuery(search?: string) {
  const sp = new URLSearchParams();
  if (search?.trim()) sp.set('search', search.trim());
  const qs = sp.toString();
  return {
    queryKey: ['admin', 'banca', 'commercialista', search?.trim() ?? ''] as const,
    queryFn: () => api.get<Commercialista>(`/admin/banca/commercialista${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
  };
}

export const COME_LABEL: Record<ComeSegnata, string> = {
  AUTOMATICO: 'Abbinata in automatico al bonifico',
  CONFERMATO: 'Abbinamento confermato a mano',
  A_MANO: 'Segnata pagata a mano',
};

// ════════════════════════════════════════════════════════════════════
// Movimenti del conto
// ════════════════════════════════════════════════════════════════════

export type TipoCollegato = 'FATTURA_CLIENTE' | 'FATTURA_FORNITORE' | 'TASSA' | 'VOCE_A_MANO';

/** Il chip della colonna "Collegato a": il tipo di documento, per esteso. */
export const COLLEGATO_LABEL: Record<TipoCollegato, string> = {
  FATTURA_CLIENTE: 'Fattura di un cliente',
  FATTURA_FORNITORE: 'Fattura di un fornitore',
  TASSA: 'Tassa',
  VOCE_A_MANO: 'Voce a mano',
};

/** Le stesse parole per le uscite ancora aperte fra cui scegliere in "Collega". */
export const USCITA_LABEL: Record<UscitaSource, string> = {
  PAYABLE: 'Fattura di un fornitore',
  RECURRING: 'Spesa fissa',
  TAX: 'Tassa',
  CASH: 'Voce a mano',
};

export interface MovimentoConto {
  id: string;
  accountId: string;
  accountName: string | null;
  side: 'DEBIT' | 'CREDIT';
  /** null quando il movimento è riservato (stipendio, e chi guarda non ha il permesso). */
  amount: number | null;
  label: string;
  counterparty: string | null;
  operationType: string | null;
  /** Data di accredito, "YYYY-MM-DD". */
  settledOn: string | null;
  match: { kind: MatchKind; id: string } | null;
  collegato: { tipo: TipoCollegato; cosa: string | null } | null;
  stipendio: { tipo: PayrollKind; etichetta: string; persona: string | null; mese: string | null } | null;
  riservato: boolean;
}

export interface MovimentiParams {
  accountId?: string;
  from?: string;
  to?: string;
  side?: 'DEBIT' | 'CREDIT' | '';
  senzaDocumento?: boolean;
  search?: string;
  /** Ordina per (24/9): 'recenti' (predefinito), 'vecchi', 'alti', 'bassi'. */
  sort?: string;
  includeHistory?: boolean;
  page?: number;
  pageSize?: number;
}

export function movimentiQuery(p: MovimentiParams) {
  const sp = new URLSearchParams();
  if (p.accountId) sp.set('accountId', p.accountId);
  if (p.from) sp.set('from', p.from);
  if (p.to) sp.set('to', p.to);
  if (p.side) sp.set('side', p.side);
  if (p.senzaDocumento) sp.set('unmatched', 'true');
  if (p.search?.trim()) sp.set('search', p.search.trim());
  if (p.sort && p.sort !== 'recenti') sp.set('sort', p.sort);
  if (p.includeHistory) sp.set('includeHistory', 'true');
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return {
    queryKey: ['admin', 'banca', 'movimenti', p] as const,
    queryFn: async (): Promise<Paginated<MovimentoConto>> => {
      const r = await api.get<Paginated<MovimentoConto>>(`/admin/transactions?${sp.toString()}`);
      return {
        ...r,
        items: (r.items ?? []).map((t) => ({
          ...t,
          amount: t.amount == null ? null : Number(t.amount),
          collegato: t.collegato ?? null,
          stipendio: t.stipendio ?? null,
          riservato: Boolean(t.riservato),
        })),
      };
    },
    staleTime: 15_000,
  };
}

// ════════════════════════════════════════════════════════════════════
// Il tempo in parole ("ieri", "3 giorni fa", "da 6 mesi")
// ════════════════════════════════════════════════════════════════════

/** "3 giorni", "2 settimane", "un mese", "4 anni": quanto tempo, senza "fa" né "tra". */
export function durata(giorni: number): string {
  const g = Math.abs(Math.round(giorni));
  if (g <= 1) return 'un giorno';
  if (g < 14) return `${g} giorni`;
  if (g < 60) { const s = Math.round(g / 7); return s === 1 ? 'una settimana' : `${s} settimane`; }
  if (g < 365) { const m = Math.round(g / 30.44); return m === 1 ? 'un mese' : `${m} mesi`; }
  const a = Math.floor(g / 365);
  return a === 1 ? 'un anno' : `${a} anni`;
}

/** "oggi", "ieri", "3 giorni fa", "domani", "tra 2 settimane". La data esatta va nel title. */
export function tempoFa(iso: string | null | undefined): string {
  const d = daysFromToday(iso);
  if (d == null) return '';
  if (d === 0) return 'oggi';
  if (d === -1) return 'ieri';
  if (d === 1) return 'domani';
  return d < 0 ? `${durata(-d)} fa` : `tra ${durata(d)}`;
}

/** Per una scadenza: "scade oggi", "scaduta ieri", "scaduta da 6 mesi", "scade tra 3 giorni". */
export function scadenzaInParole(iso: string | null | undefined): string {
  const d = daysFromToday(iso);
  if (d == null) return 'senza scadenza';
  if (d === 0) return 'scade oggi';
  if (d === -1) return 'scaduta ieri';
  if (d === 1) return 'scade domani';
  return d < 0 ? `scaduta da ${durata(-d)}` : `scade tra ${durata(d)}`;
}

/** "4 settembre 2026": la data esatta, detta come si dice. */
export function dataLunga(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, g] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !g) return '';
  return new Date(y, m - 1, g).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** "oggi alle 10:45", "ieri alle 18:02", "il 20/09/2026 alle 9:30": per "Banca aggiornata …". */
export function quandoAggiornata(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  const oggi = new Date();
  const ieri = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - 1);
  if (d.toDateString() === oggi.toDateString()) return `oggi alle ${hm}`;
  if (d.toDateString() === ieri.toDateString()) return `ieri alle ${hm}`;
  return `il ${fmtDate(d)} alle ${hm}`;
}

/** "agosto 2026" da "2026-08". */
export function meseInParole(ym: string | null | undefined): string {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

/**
 * Un importo scritto a mano: "1.234,50", "1234,5", "1234.50", "€ 80". Con la
 * virgola i punti sono migliaia; senza virgola un punto seguito da una o due
 * cifre è il decimale (chi scrive "12.50" intende 12,50, non 1.250).
 */
export function parseEuro(s: string): number {
  const t = s.trim().replace(/[€\s]/g, '');
  if (!t) return Number.NaN;
  if (t.includes(',')) return Number(t.replace(/\./g, '').replace(',', '.'));
  if (/^-?\d+\.\d{1,2}$/.test(t)) return Number(t);
  return Number(t.replace(/\./g, ''));
}

/** Un saldo piccolo con i centesimi (4,55 non è 5), uno grande senza. */
export function fmtSaldo(n: number): string {
  return Math.abs(n) < 100 && Math.round(n) !== n ? fmtEur(n, true) : fmtEur(n);
}

// ════════════════════════════════════════════════════════════════════
// Le azioni della pagina
// ════════════════════════════════════════════════════════════════════

export interface ConfermaArgs {
  lato: 'INCASSO' | 'USCITA';
  rataId: string;
  txId: string;
  paidDate: string;
  /** "Fattura 22A di C.E.D. ALICUOR" per il messaggio. */
  label: string;
}

export function useBancaActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    // Le fatture di Contabilità cambiano stato quando l'ultima rata risulta pagata.
    qc.invalidateQueries({ queryKey: ['invoices'] });
  };
  const errore = (titolo: string) => (e: any) => toast.alert(titolo, e?.message ?? 'Riprova tra un momento');

  /** "Conferma tutti gli abbinamenti sicuri" (e collega i bonifici delle fatture già pagate che tornano senza dubbi). */
  const confermaTutti = useMutation({
    mutationFn: () => api.post<{ lotto: string; chiuse: number; valore: number; agganciate: number }>('/admin/riconciliazione/applica', {}),
    onSuccess: (r) => {
      invalidate();
      const titolo = r.chiuse
        ? plurale(r.chiuse, 'abbinamento confermato', 'abbinamenti confermati')
        : r.agganciate ? plurale(r.agganciate, 'bonifico collegato', 'bonifici collegati') : 'Nessun abbinamento sicuro da confermare';
      const dettagli = [
        r.chiuse ? `${plurale(r.chiuse, 'fattura segnata pagata', 'fatture segnate pagate')} per € ${fmtEur(r.valore)}` : null,
        r.chiuse && r.agganciate ? `${plurale(r.agganciate, 'bonifico collegato', 'bonifici collegati')} a fatture già pagate` : null,
      ].filter(Boolean).join(' · ');
      toast.undo(titolo.charAt(0).toUpperCase() + titolo.slice(1), {
        description: dettagli || undefined,
        onUndo: () => {
          api.post('/admin/riconciliazione/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok('Abbinamenti annullati', 'Tutto è tornato com’era prima.'); })
            .catch(errore('Annullamento non riuscito'));
        },
      });
    },
    onError: errore('Abbinamenti non confermati'),
  });

  /** Conferma un abbinamento: la fattura risulta pagata qui, con quel bonifico. */
  const conferma = useMutation({
    mutationFn: (v: ConfermaArgs) =>
      api.post('/admin/riconciliazione/conferma', { lato: v.lato, rataId: v.rataId, txId: v.txId, paidDate: v.paidDate }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.ok('Abbinamento confermato', `${v.label}: ${v.lato === 'INCASSO' ? 'incassata' : 'pagata'} con il bonifico.`);
    },
    onError: errore('Abbinamento non confermato'),
  });

  /** Fattura già pagata: si collega il bonifico e basta, senza cambiare niente della fattura. */
  const collega = useMutation({
    mutationFn: (v: { lato: 'INCASSO' | 'USCITA'; rataId: string; txId: string; label: string }) =>
      api.post('/admin/riconciliazione/collega', { lato: v.lato, rataId: v.rataId, txId: v.txId }),
    onSuccess: (_r, v) => { invalidate(); toast.ok('Bonifico collegato', v.label); },
    onError: errore('Bonifico non collegato'),
  });

  /** "Non è questo il bonifico giusto": la coppia non si ripropone più. */
  const scarta = useMutation({
    mutationFn: (v: { rataId: string; txId: string; label: string }) => api.post('/admin/riconciliazione/scarta', { rataId: v.rataId, txId: v.txId }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.undo('Abbinamento scartato', {
        description: v.label,
        onUndo: () => {
          api.post('/admin/riconciliazione/ripristina-scarto', { rataId: v.rataId, txId: v.txId })
            .then(() => invalidate())
            .catch(errore('Abbinamento non rimesso in lista'));
        },
      });
    },
    onError: errore('Abbinamento non scartato'),
  });

  const rimetti = useMutation({
    mutationFn: (v: { rataId: string; txId: string }) => api.post('/admin/riconciliazione/ripristina-scarto', v),
    onSuccess: () => { invalidate(); toast.ok('Abbinamento rimesso in lista'); },
    onError: errore('Abbinamento non rimesso in lista'),
  });

  /** Annulla una conferma in blocco ("Conferma tutti gli abbinamenti sicuri"; i "lotti" di prima: la parola non esce più). */
  const annullaAutomatici = useMutation({
    mutationFn: async (v: { lotto: string; chiuse: number; agganciate: number }) => {
      const ok = await confirmDialog({
        title: 'Annullare questa conferma in blocco?',
        body: `${plurale(v.chiuse, 'fattura torna', 'fatture tornano')} com’era prima, con la data e il conto di prima, e ${plurale(v.chiuse + v.agganciate, 'bonifico si stacca', 'bonifici si staccano')} dai documenti.`,
        confirmLabel: 'Annulla la conferma in blocco',
        icon: 'undo',
        danger: true,
      });
      if (!ok) return null;
      return api.post('/admin/riconciliazione/annulla', { lotto: v.lotto });
    },
    onSuccess: (r) => { if (!r) return; invalidate(); toast.ok('Conferma in blocco annullata', 'Le fatture sono tornate com’erano prima.'); },
    onError: errore('Annullamento non riuscito'),
  });

  /** Le rate delle auto pagate in banca e mai registrate: diventano uscite già pagate. */
  const registraRateAuto = useMutation({
    mutationFn: async (v: { recurringId: string; nome: string; addebiti: number; totale: number }) => {
      const ok = await confirmDialog({
        title: `Registrare ${plurale(v.addebiti, 'rata', 'rate')} di ${v.nome}?`,
        body: `Sono addebiti veri sul conto, per € ${fmtEur(v.totale)}, che oggi non risultano da nessuna parte. Diventano uscite già pagate, con la data e l’importo del movimento. Si può annullare.`,
        confirmLabel: 'Registra le rate',
        icon: 'repeat',
      });
      if (!ok) return null;
      return api.post<{ create: number; totale: number; lotto: string }>('/admin/riconciliazione/rate-mancanti/crea', { recurringId: v.recurringId });
    },
    onSuccess: (r) => {
      if (!r) return;
      invalidate();
      toast.undo(`${plurale(r.create, 'rata registrata', 'rate registrate')}`, {
        description: `€ ${fmtEur(r.totale)}`,
        onUndo: () => {
          api.post('/admin/riconciliazione/rate-mancanti/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok('Rate tolte', 'Tutto è tornato com’era prima.'); })
            .catch(errore('Annullamento non riuscito'));
        },
      });
    },
    onError: errore('Rate non registrate'),
  });

  /** Movimenti del conto › "Collega a un documento": collega e segna pagato alla data del movimento. */
  const collegaMovimento = useMutation({
    mutationFn: ({ transactionId, kind, id }: { transactionId: string; kind: MatchKind; id: string; label: string }) =>
      api.post(`/admin/transactions/${transactionId}/match`, { kind, id, markPaid: true }),
    onSuccess: (_r, v) => { invalidate(); toast.ok('Movimento collegato', v.label); },
    onError: errore('Movimento non collegato'),
  });

  /**
   * Una spesa fissa non ha ancora la sua voce: la si paga con questo movimento,
   * ed è quello il collegamento. L'importo registrato è quello del movimento
   * (lo decide l'API quando c'è `bankTransactionId`), non quello previsto.
   */
  const collegaSpesaFissa = useMutation({
    mutationFn: (v: { id: string; dueDate: string | null; paidDate: string; transactionId: string; method?: string; label: string }) =>
      api.post('/admin/uscite/pay', {
        source: 'RECURRING', id: v.id, paidDate: v.paidDate, method: v.method ?? 'BONIFICO',
        dueDate: v.dueDate || undefined, bankTransactionId: v.transactionId,
      }),
    onSuccess: (_r, v) => { invalidate(); toast.ok('Movimento collegato alla spesa fissa', v.label); },
    onError: errore('Movimento non collegato'),
  });

  /**
   * "Rimetti da pagare" su un pagamento segnato in automatico: la voce torna
   * com'era e il movimento si stacca. Non si richiude più da sola con quel
   * movimento: da lì in poi si conferma a mano.
   */
  const annullaAutomatico = useMutation({
    mutationFn: (v: PagamentoAutomatico) => api.post('/admin/banca/automatici/annulla', { runKey: v.runKey, key: v.key }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.ok(
        v.tipo === 'INCASSO' ? 'Fattura rimessa da incassare' : v.tipo === 'USCITA' ? 'Fattura rimessa da pagare' : 'Spesa fissa rimessa da pagare',
        `${v.nome}: il movimento in banca è di nuovo libero e non si abbina più da solo.`,
      );
    },
    onError: errore('Pagamento non rimesso com’era'),
  });

  const scollegaMovimento = useMutation({
    mutationFn: ({ transactionId }: { transactionId: string; label: string }) => api.delete(`/admin/transactions/${transactionId}/match`),
    onSuccess: (_r, v) => { invalidate(); toast.ok('Documento scollegato dal movimento', v.label); },
    onError: errore('Documento non scollegato'),
  });

  /** "Correggi i contanti": quanti soldi ci sono in cassa oggi, contati a mano. */
  const correggiContanti = useMutation({
    mutationFn: (saldo: number) => api.post<{ prima: number; saldo: number }>('/admin/banca/contanti', { saldo }),
    onSuccess: (r) => { invalidate(); toast.ok('Contanti aggiornati', `In cassa adesso: € ${fmtSaldo(r.saldo)}`); },
    onError: errore('Contanti non aggiornati'),
  });

  return {
    confermaTutti, conferma, collega, scarta, rimetti, annullaAutomatici, registraRateAuto,
    collegaMovimento, collegaSpesaFissa, scollegaMovimento, correggiContanti, annullaAutomatico,
  };
}
